import styles from './EditarBloc.module.scss';
import { useCallback, useEffect, useState } from 'react';
import PanelColumn from '../PanelColumn/PanelColumnComponent';
import RightSidePanelContent from '../RightSidePanelContent/RightSidePanelContentComponent';
import Flex from '../Flex/FlexComponent';
import Button from '../Button/ButtonComponent';
import { useGetFileTreeUrl } from '../../utils/hooks/useGetFileTreeUrl';
import { getData } from '@portal-internet/bff';
import { useQuery, useQueryClient } from 'react-query';
import { useRouter } from 'next/router';
import AfegirBlocImatgeComponent from '../AfegirBlocImatge';
import EditarFontDeDadesComponent from '../EditarFontDeDades';
import AfegirBlocFormulariComponent from '../AfegirBlocFormulari';
import { useRedlMetadata } from 'hooks/useRedlMetadata';
import { useRedl } from 'hooks/useRedl';
import { getEditableInfo } from '../../utils/getEditableInfo';
import { normalizeEditableInfo } from '../../utils/normalizeEditableInfo';
import __get from 'lodash/get';
import __omit from 'lodash/omit';
import __set from 'lodash/set';
import __cloneDeep from 'lodash/cloneDeep';
import __mapKeys from 'lodash/mapKeys';
import __merge from 'lodash/merge';
import __flatMap from 'lodash/flatMap';
import __has from 'lodash/has';
import __unset from 'lodash/unset';
import Ajv from 'ajv';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinnerREDL';

export default function EditarBlocComponent() {
    const { showRightSidePanel, setShowRightSidePanel } = useRedl();
    const [newBlock, setNewBlock] = useState(showRightSidePanel.action === 'edit' ? showRightSidePanel.data : {});
    const router = useRouter();
    const queryClient = useQueryClient();
    const queryKey = 'fileTreeRoot';
    const { addRedlBloc, editRedlBloc, getRedlPageMetadata } = useRedlMetadata(router.pathname);
    const { components, blocREDLSchema } = getRedlPageMetadata(router.pathname);

    const ajv = new Ajv();
    const schemaValidator = ajv.compile(blocREDLSchema);
    const getFileTreeUrl = useGetFileTreeUrl();
    const isBlocREDLValid = schemaValidator(newBlock);

    const editableInfoRaw = getEditableInfo(newBlock || {}, components, [], '');
    const editableInfo = normalizeEditableInfo(editableInfoRaw);

    const { data, isFetched } = useQuery(
        [
            queryKey,
            {
                url: getFileTreeUrl(showRightSidePanel.data?.pathname, showRightSidePanel.data?.filename)
            }
        ],
        getData,
        {
            enabled:
                Boolean(showRightSidePanel.action === 'addDefault') &&
                Boolean(showRightSidePanel.data?.pathname) &&
                Boolean(showRightSidePanel.data?.filename)
        }
    );

    const handleSave = () => {
        // add source if it hasn't been edited but is new
        const newSources = __cloneDeep(showRightSidePanel.sources || {});
        __mapKeys(__get(editableInfo, '[0].sources'), (value, key) => {
            if (!__get(newSources, key)) {
                __set(newSources, key, value);
            }
        });

        // scan all sources on all components
        const defaultBlockSources = editableInfo.reduce((result, item) => {
            if (item.sources) {
                __mapKeys(item.sources, (value, key) => {
                    result[key] = value;
                });
            }
            return result;
        }, {});

        __merge(newSources, defaultBlockSources);

        if (showRightSidePanel.action === 'edit') {
            editRedlBloc(newBlock, showRightSidePanel.blocIdx - 1, newSources);
        } else {
            addRedlBloc(newBlock, showRightSidePanel.blocIdx - 1, newSources);
        }
        closePanel();
    };

    const handleFontDeDadesSave = ({ newSource, oldSource, propagateSource }) => {
        setNewBlock((prevState) => {
            // delete old data source
            const paths = propagateSource ? editableInfo.map((ei) => ei.path) : [showRightSidePanel.fontDeDades?.path];
            let newState = prevState;

            paths.forEach((path) => {
                const source = `${path}.sources.${oldSource.name}`;
                if (__has(newState, source)) {
                    const updatedSource = __get(prevState, source);
                    __unset(newState, source);

                    // add new data source
                    __set(updatedSource, 'url', newSource.url);
                    __set(newState, `${path}.sources.${newSource.value}`, updatedSource);

                    // update references to old data source with new one
                    const fields = __get(newState, path);
                    Object.keys(fields?.props || {}).forEach((key) => {
                        if (fields.props[key]?.dataId === oldSource.name) {
                            __set(newState, `${path}.props.${key}.dataId`, newSource.value);
                        }
                    });
                    __set(newState, `${path}.props.dataId`, newSource.value);
                }
            });

            return { ...newState };
        });
        setShowRightSidePanel((prevState) => {
            // delete old data source
            const newState = __omit(prevState, [`sources.${oldSource.name}`]);
            // add new data source
            __set(newState, `sources.${newSource.value}`, { url: newSource.url });
            return { ...newState, size: showRightSidePanel?.action === 'edit' ? 'md' : 'xl', fontDeDades: undefined };
        });
    };

    const closePanel = () => {
        setShowRightSidePanel(null);
        queryClient.invalidateQueries(queryKey);
    };

    const addConfigProps = useCallback((data) => {
        const dataWithConfigProps = __flatMap(data, (componenti) => {
            if (componenti.name && componenti.config) {
                const componentConfigs = components.find((c) => c.name === componenti.name);
                if (componentConfigs?.config && componentConfigs?.config[componenti.config]) {
                    const clonedProps = __cloneDeep(componentConfigs.config[componenti.config].props);
                    componenti.props = {
                        ...clonedProps,
                        ...componenti.props
                    };
                }
                delete componenti.config;
            }
            if (componenti.children) {
                componenti.children = addConfigProps(componenti.children);
            }
            return componenti;
        });

        return dataWithConfigProps;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const newBlockJson = [newBlock?.props?.path, '.json'].join('');
        const showRightSidePanelJson = [showRightSidePanel.data?.pathname, showRightSidePanel.data?.filename].join('/');
        if (data && isFetched && newBlockJson !== showRightSidePanelJson) {
            const novaData = addConfigProps([data])[0];
            setNewBlock({
                ...novaData,
                props: {
                    ...novaData?.props,
                    thumbnail: `${showRightSidePanel?.data?.pathname}/${showRightSidePanel?.data?.thumbnail}`
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isFetched, showRightSidePanel]);

    if (!newBlock) return null;

    if (!newBlock?.props && !isBlocREDLValid) {
        return (
            <div className={styles.loading}>
                <LoadingSpinner message='Carregant' />
            </div>
        );
    }

    return (
        <RightSidePanelContent title={`Mòdul preconfigurat ${newBlock?.props?.descripcio}`}>
            <Flex container columnGap='8px' flex='1' position='relative' overflow='hidden'>
                <PanelColumn length={1} visible>
                    <Flex container flexDirection='column' height='100%' overflow='hidden'>
                        <Flex flex='0 0 auto'>
                            <AfegirBlocImatgeComponent
                                data={
                                    showRightSidePanel.action === 'edit'
                                        ? { thumbnail: `${newBlock.props?.path}.png` }
                                        : newBlock.props
                                }
                            />
                        </Flex>
                        <Flex flex='1' margin='16px 0 0 0' overflow='hidden'>
                            <AfegirBlocFormulariComponent
                                editableInfo={editableInfo}
                                newBlock={newBlock}
                                setNewBlock={setNewBlock}
                            />
                        </Flex>
                    </Flex>
                </PanelColumn>
                <PanelColumn
                    visible={showRightSidePanel?.size === 'xl' && showRightSidePanel?.fontDeDades !== undefined}
                    size={showRightSidePanel?.fontDeDades !== undefined ? showRightSidePanel?.size : null}
                    length={1}
                    sibling
                >
                    <EditarFontDeDadesComponent editableInfo={editableInfo} handleSave={handleFontDeDadesSave} />
                </PanelColumn>
            </Flex>
            {!isBlocREDLValid && (
                <Flex container alignItems='center' flex='0 0 auto' justifyContent='center' padding='16px 0 0 0'>
                    <div className={styles.error}>
                        <p>Aquest bloc té algun error i no es pot afegir.</p>
                    </div>
                </Flex>
            )}
            <Flex container alignItems='center' flex='0 0 auto' justifyContent='center' padding='16px 0 0 0'>
                <Button variant='tertiary' size='md' onClick={closePanel}>
                    Cancel·la
                </Button>
                <Button
                    variant='primary'
                    size='md'
                    disabled={!isBlocREDLValid}
                    onClick={handleSave}
                    data-testid='REDL-apply-button'
                >
                    Aplica
                </Button>
            </Flex>
        </RightSidePanelContent>
    );
}
